import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import Sheet from "components/sheet/Sheet";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import Spin from "components/spin/spin";
import { TMergeDataCategory, TMergeDataKey } from "./loadMergeAccountData";
import "./mergeCustomers.scss";

export interface IMergeChooseAccountDataProps {
  open: boolean;
  closeCustomerName: string;
  keepCustomerName: string;
  categories: TMergeDataCategory[];
  loading?: boolean;
  onCancel: () => void;
  onBack: () => void;
  onReview: (categories: TMergeDataCategory[]) => void;
  onChange: (categories: TMergeDataCategory[]) => void;
}

export default function MergeChooseAccountData(props: IMergeChooseAccountDataProps) {
  const { t } = useTranslation();
  const { open, closeCustomerName, keepCustomerName, categories, loading, onCancel, onBack, onReview, onChange } =
    props;
  const [expanded, setExpanded] = useState<Partial<Record<TMergeDataKey, boolean>>>({});

  const selectedCount = useMemo(() => categories.filter(category => category.selected).length, [categories]);
  const allSelected = selectedCount === categories.length && categories.length > 0;

  function toggleCategory(key: TMergeDataKey) {
    onChange(
      categories.map(category =>
        category.key === key ? { ...category, selected: !category.selected } : category,
      ),
    );
  }

  function toggleSelectAll() {
    const nextSelected = !allSelected;
    onChange(categories.map(category => ({ ...category, selected: nextSelected })));
  }

  function clearAll() {
    onChange(categories.map(category => ({ ...category, selected: false })));
  }

  function toggleExpanded(key: TMergeDataKey) {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <Sheet
      title={t("Choose account data")}
      supportText={t("Select the data to transfer from {{closeName}} into {{keepName}}'s account.", {
        closeName: closeCustomerName,
        keepName: keepCustomerName,
      })}
      open={open}
      size="modal"
      closable
      stacked
      onCancel={onBack}
      onOk={() => onReview(categories)}
      okText={t("Review merge")}
      cancelText={t("Go back")}
      onClose={onCancel}
      okDisabled={loading || categories.length === 0}
      height="flexible"
    >
      {loading ? (
        <div className="merge-customers__loading">
          <Spin />
        </div>
      ) : (
        <div className="merge-customers-choose">
          <div className="merge-customers-choose__select-row">
            <Checkbox
              size="medium"
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              onChange={toggleSelectAll}
              label={t("{{count}} selected", { count: selectedCount })}
            />
            <Button type="text" size="small" onClick={clearAll} disabled={selectedCount === 0}>
              {t("Clear all")}
            </Button>
          </div>

          <div className="merge-customers-choose__list">
            {categories.map(category => {
              const isExpanded = !!expanded[category.key];
              const showAccordion = !!category.accordion;

              return (
                <div
                  key={category.key}
                  className={classNames("merge-customers-choose__item", {
                    "merge-customers-choose__item--selected": category.selected,
                    "merge-customers-choose__item--expanded": isExpanded,
                  })}
                >
                  <div className="merge-customers-choose__item-header">
                    <div className="merge-customers-choose__checkbox">
                      <Checkbox
                        size="medium"
                        checked={category.selected}
                        onChange={() => toggleCategory(category.key)}
                        label={
                          <span className="merge-customers-choose__label-wrap">
                            <span className="merge-customers-choose__label">
                              {t(category.label)}
                              {typeof category.count === "number" ? (
                                <span className="merge-customers-choose__count"> ({category.count})</span>
                              ) : null}
                            </span>
                            {category.summary ? (
                              <span className="merge-customers-choose__summary">{category.summary}</span>
                            ) : null}
                          </span>
                        }
                      />
                    </div>
                    {showAccordion ? (
                      <button
                        type="button"
                        className="merge-customers-choose__chevron"
                        aria-label={isExpanded ? t("Collapse") : t("Expand")}
                        onClick={() => toggleExpanded(category.key)}
                      >
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                      </button>
                    ) : null}
                  </div>

                  {showAccordion && isExpanded ? (
                    <ul className="merge-customers-choose__accordion-list">
                      {(category.items || []).map(item => (
                        <li key={item.id} className="merge-customers-choose__accordion-item">
                          <span className="merge-customers-choose__accordion-bullet" aria-hidden />
                          <div className="merge-customers-choose__accordion-text">
                            <span className="merge-customers-choose__accordion-title">{item.title}</span>
                            {item.subtitle ? (
                              <span className="merge-customers-choose__accordion-subtitle">{item.subtitle}</span>
                            ) : null}
                          </div>
                        </li>
                      ))}
                      {!category.items?.length ? (
                        <li className="merge-customers-choose__accordion-empty">{t("No items")}</li>
                      ) : null}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Sheet>
  );
}
